import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ChevronRight, Lock, Star, AlertTriangle, CheckCircle, BookOpen } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const TORIBIO_PHOTO = 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/g4e6rkdn_1426B748-A180-4338-9F16-39392E5C61F1.png';

const RodriguezStoryMode = ({ gameId, onBack }) => {
  const [story, setStory] = useState(null);
  const [progress, setProgress] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const [activeMission, setActiveMission] = useState(null);
  const [missionResult, setMissionResult] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [storyRes, progressRes] = await Promise.all([
        fetch(`${API}/api/police/story`),
        fetch(`${API}/api/police/story/${gameId}/progress`),
      ]);
      if (storyRes.ok) setStory(await storyRes.json());
      if (progressRes.ok) setProgress(await progressRes.json());
    } catch (e) {}
    setLoading(false);
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const playMission = async (missionId, choiceId) => {
    setPlaying(true);
    setMissionResult(null);
    try {
      const body = { mission_id: missionId };
      if (choiceId) body.choice_id = choiceId;
      const res = await fetch(`${API}/api/police/story/${gameId}/mission`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (res.ok && d.success) {
        setMissionResult(d);
        load();
      } else {
        setMissionResult({ error: d.detail || 'Failed' });
      }
    } catch (e) { setMissionResult({ error: 'Network error' }); }
    setPlaying(false);
  };

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-8">Loading...</p>;
  if (!story) return null;

  // Active mission view
  if (activeMission && activeChapter) {
    const ch = story.chapters.find(c => c.id === activeChapter);
    const mission = ch?.missions.find(m => m.id === activeMission);
    if (!mission) return null;

    const isCompleted = progress?.completed_missions?.includes(mission.id);
    const isChoice = mission.type === 'choice' && mission.choices;

    return (
      <div className="space-y-4" data-testid="story-mission">
        <button onClick={() => { setActiveMission(null); setMissionResult(null); }}
          className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#1E90FF]">
          BACK TO CHAPTER
        </button>

        <div className="bg-[#0a0e12] border border-[#1E90FF]/20 rounded-xl overflow-hidden">
          <div className="relative h-40">
            <img src={ch.image} alt="" className="w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e12] to-transparent" />
            <div className="absolute bottom-3 left-4">
              <p className="font-['Outfit'] text-[#1E90FF] text-[9px] tracking-widest">{ch.era}</p>
              <h4 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#E8E0D0]">{mission.name}</h4>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <p className="font-['Outfit'] text-[#E8E0D0]/60 text-sm leading-relaxed">{mission.desc}</p>

            {isCompleted ? (
              <div className="flex items-center gap-2 px-4 py-3 bg-[#4ADE80]/5 border border-[#4ADE80]/20 rounded-lg">
                <CheckCircle size={16} className="text-[#4ADE80]" />
                <span className="font-['Outfit'] text-[#4ADE80] text-xs">Mission Complete</span>
                {progress?.choices_made?.[mission.id] && (
                  <span className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] ml-auto">
                    Choice: {progress.choices_made[mission.id]}
                  </span>
                )}
              </div>
            ) : missionResult?.success ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="space-y-3 bg-[#1E90FF]/5 border border-[#1E90FF]/20 rounded-xl p-4">
                <h5 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1E90FF]">MISSION COMPLETE</h5>
                <p className="font-['Outfit'] text-[#E8E0D0]/60 text-xs">{missionResult.narrator}</p>
                <div className="flex gap-3">
                  {missionResult.rewards?.xp > 0 && <span className="font-['Outfit'] text-[9px] text-[#C9A84C]">+{missionResult.rewards.xp} XP</span>}
                  {missionResult.rewards?.cash > 0 && <span className="font-['Outfit'] text-[9px] text-[#4ADE80]">+${missionResult.rewards.cash.toLocaleString()}</span>}
                </div>
                <div className="flex gap-4 text-[9px] font-['Outfit']">
                  <span className="text-[#1E90FF]">Integrity: {missionResult.integrity}/10</span>
                  <span className={missionResult.ia_scrutiny >= 7 ? 'text-[#DC2626]' : 'text-[#E8E0D0]/40'}>IA Scrutiny: {missionResult.ia_scrutiny}/10</span>
                </div>
                {missionResult.chapter_complete && (
                  <div className="text-center pt-2 border-t border-[#1E90FF]/10">
                    <p className="font-['Cormorant_Garamond'] text-sm text-[#FFD700] italic">Chapter Complete. The next chapter awaits.</p>
                  </div>
                )}
              </motion.div>
            ) : missionResult?.error ? (
              <p className="font-['Outfit'] text-[#DC2626] text-xs">{missionResult.error}</p>
            ) : isChoice ? (
              <div className="space-y-2">
                <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[9px] tracking-widest text-center">CHOOSE YOUR PATH</p>
                {mission.choices.map(c => (
                  <button key={c.id} onClick={() => playMission(mission.id, c.id)} disabled={playing}
                    className="w-full px-4 py-3 bg-[#111820] border border-[#1E90FF]/15 rounded-lg text-left hover:border-[#1E90FF]/40 transition-all group"
                    data-testid={`choice-${c.id}`}>
                    <p className="font-['Outfit'] text-sm text-[#E8E0D0] group-hover:text-[#1E90FF] transition-colors">{c.text}</p>
                    <div className="flex gap-3 mt-1">
                      {c.integrity_change !== 0 && (
                        <span className={`font-['Outfit'] text-[8px] ${c.integrity_change > 0 ? 'text-[#4ADE80]' : 'text-[#DC2626]'}`}>
                          Integrity {c.integrity_change > 0 ? '+' : ''}{c.integrity_change}
                        </span>
                      )}
                      {c.ia_change && (
                        <span className={`font-['Outfit'] text-[8px] ${c.ia_change < 0 ? 'text-[#4ADE80]' : 'text-[#DC2626]'}`}>
                          IA Scrutiny {c.ia_change > 0 ? '+' : ''}{c.ia_change}
                        </span>
                      )}
                      {c.cash_bonus > 0 && <span className="font-['Outfit'] text-[8px] text-[#C9A84C]">+${c.cash_bonus.toLocaleString()}</span>}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <button onClick={() => playMission(mission.id)} disabled={playing}
                className="w-full py-3 bg-[#1E90FF]/10 border border-[#1E90FF]/30 rounded-lg text-[#1E90FF] font-['Outfit'] text-xs tracking-widest hover:bg-[#1E90FF]/20 disabled:opacity-30"
                data-testid="play-mission">
                {playing ? 'IN PROGRESS...' : 'BEGIN MISSION'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Chapter detail view
  if (activeChapter) {
    const ch = story.chapters.find(c => c.id === activeChapter);
    const chProgress = progress?.chapters?.find(c => c.id === activeChapter);
    if (!ch) return null;

    return (
      <div className="space-y-4" data-testid="story-chapter">
        <button onClick={() => setActiveChapter(null)}
          className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#1E90FF]">
          BACK TO CHAPTERS
        </button>

        <div className="relative h-48 rounded-xl overflow-hidden">
          <img src={ch.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <p className="font-['Outfit'] text-[#1E90FF] text-[9px] tracking-widest">{ch.era} — CHAPTER {ch.number}</p>
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#E8E0D0]">{ch.title}</h3>
            <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs mt-0.5">{ch.subtitle}</p>
          </div>
        </div>

        <p className="font-['Outfit'] text-[#E8E0D0]/50 text-xs leading-relaxed">{ch.description}</p>

        <div className="space-y-2">
          <p className="font-['Outfit'] text-[#1E90FF] text-[9px] tracking-[0.3em]">MISSIONS</p>
          {ch.missions.map((m, i) => {
            const completed = progress?.completed_missions?.includes(m.id);
            const prevCompleted = i === 0 || progress?.completed_missions?.includes(ch.missions[i-1].id);
            return (
              <motion.button key={m.id}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => prevCompleted && setActiveMission(m.id)}
                disabled={!prevCompleted}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                  completed ? 'bg-[#4ADE80]/5 border-[#4ADE80]/20' :
                  prevCompleted ? 'bg-[#111820] border-[#1E90FF]/15 hover:border-[#1E90FF]/40' :
                  'bg-[#0a0c10] border-[#1A2535] opacity-40'
                }`}
                data-testid={`mission-${m.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      completed ? 'bg-[#4ADE80]/20' : prevCompleted ? 'bg-[#1E90FF]/10' : 'bg-[#1A2535]'
                    }`}>
                      {completed ? <CheckCircle size={14} className="text-[#4ADE80]" /> :
                       prevCompleted ? <span className="font-['Outfit'] text-[10px] text-[#1E90FF] font-bold">{i+1}</span> :
                       <Lock size={12} className="text-[#E8E0D0]/15" />}
                    </div>
                    <div>
                      <p className={`font-['Outfit'] text-xs font-semibold ${completed ? 'text-[#4ADE80]' : 'text-[#E8E0D0]'}`}>{m.name}</p>
                      <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">{m.desc.slice(0, 60)}...</p>
                    </div>
                  </div>
                  {!completed && prevCompleted && <ChevronRight size={14} className="text-[#1E90FF]/30" />}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    );
  }

  // Chapter list view (main)
  return (
    <div className="space-y-4" data-testid="rodriguez-story">
      <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#1E90FF]" data-testid="story-back">BACK</button>

      {/* Header */}
      <div className="relative rounded-xl overflow-hidden" style={{ height: '200px' }}>
        <img src={TORIBIO_PHOTO} alt="" className="w-full h-full object-cover" style={{ objectPosition: '50% 15%' }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <div className="inline-flex items-center gap-2 bg-[#1E90FF]/10 border border-[#1E90FF]/30 px-3 py-1 rounded-full mb-2">
            <BookOpen size={10} className="text-[#1E90FF]" />
            <span className="font-['Outfit'] text-[8px] text-[#1E90FF] tracking-widest">STORY MODE</span>
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#E8E0D0] drop-shadow-lg">{story.title}</h2>
          <p className="font-['Outfit'] text-[#E8E0D0]/50 text-xs">{story.subtitle}</p>
        </div>
      </div>

      {/* Integrity / IA meters */}
      {progress && (
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-[#111820] border border-[#1E90FF]/15 rounded-lg p-2.5 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">INTEGRITY</p>
            <div className="flex items-center justify-center gap-1 mt-1">
              {[...Array(10)].map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full ${i < progress.integrity ? 'bg-[#1E90FF]' : 'bg-[#1A2535]'}`} />
              ))}
            </div>
            <p className="font-['Outfit'] text-[9px] text-[#1E90FF] mt-1">{progress.integrity}/10</p>
          </div>
          <div className="bg-[#111820] border border-[#DC2626]/15 rounded-lg p-2.5 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">IA SCRUTINY</p>
            <div className="flex items-center justify-center gap-1 mt-1">
              {[...Array(10)].map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full ${i < progress.ia_scrutiny ? 'bg-[#DC2626]' : 'bg-[#1A2535]'}`} />
              ))}
            </div>
            <p className={`font-['Outfit'] text-[9px] mt-1 ${progress.ia_scrutiny >= 7 ? 'text-[#DC2626]' : 'text-[#E8E0D0]/40'}`}>{progress.ia_scrutiny}/10</p>
          </div>
        </div>
      )}

      {/* Chapters */}
      <div className="space-y-3">
        <p className="font-['Outfit'] text-[#1E90FF] text-[9px] tracking-[0.3em]">CHAPTERS ({story.available_chapters}/{story.total_chapters})</p>
        {story.chapters.map((ch, i) => {
          const chProg = progress?.chapters?.find(c => c.id === ch.id);
          const unlocked = chProg?.unlocked ?? (i === 0);
          const complete = chProg?.complete ?? false;

          return (
            <motion.button key={ch.id}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              onClick={() => unlocked && setActiveChapter(ch.id)}
              disabled={!unlocked}
              className="w-full text-left rounded-xl overflow-hidden border transition-all group"
              style={{ borderColor: complete ? '#4ADE8030' : unlocked ? '#1E90FF20' : '#1A2535' }}
              data-testid={`chapter-${ch.id}`}>
              <div className="relative h-32">
                <img src={ch.image} alt="" className={`w-full h-full object-cover transition-transform duration-700 ${
                  unlocked ? 'group-hover:scale-105' : 'grayscale-[60%] opacity-50'}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
                {!unlocked && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Lock size={20} className="text-[#E8E0D0]/15" />
                  </div>
                )}
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-['Outfit'] text-[8px] text-[#1E90FF]/60 tracking-widest">{ch.era} — CHAPTER {ch.number}</p>
                      <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0]">{ch.title}</h4>
                      <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">{ch.subtitle}</p>
                    </div>
                    {complete ? (
                      <CheckCircle size={18} className="text-[#4ADE80]" />
                    ) : unlocked && chProg ? (
                      <span className="font-['Outfit'] text-[8px] text-[#1E90FF] bg-[#1E90FF]/10 px-2 py-0.5 rounded-full">
                        {chProg.completed_missions}/{chProg.total_missions}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </motion.button>
          );
        })}

        {/* Finale indicator for Chapter 7 */}
        {story.chapters.some(ch => ch.is_finale) && (
          <div className="px-4 py-3 bg-[#DC2626]/5 border border-[#DC2626]/20 rounded-xl text-center">
            <p className="font-['Outfit'] text-[9px] text-[#DC2626]/60 tracking-widest">CHAPTER 7 — PERMANENT CHOICE</p>
            <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/25 mt-0.5">Your final path cannot be undone. Choose wisely.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RodriguezStoryMode;
